import { createElement, type RefObject, useEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import cx from "classnames";
import { useAtomValueRawSync } from "jotai";
import AiToolboxSection from "src/business-components/Toolbox/components/AiToolboxSection/AiToolboxSection";
import Toolbox from "src/business-components/Toolbox/Toolbox";
import type { ToolboxApiRef } from "src/business-components/Toolbox/Toolbox.types";
import { ResizeHandle } from "src/components/general/ResizeHandle/ResizeHandle";
import { usePanelResize } from "src/hooks/usePanelResize/usePanelResize";
import { ChatSparkleIcon } from "@ui-icons";
import { toolboxConfigAtom } from "./AppToolboxSlot.atoms";
import {
  AI_TOOLBOX_SECTION_VALUE,
  INITIAL_FLEX_VALUES,
  TOOLBOX_RESIZE_RANGE,
  TOOLBOX_SEARCH_PARAM,
} from "./AppToolboxSlot.constants";
import { useAppToolboxState } from "./AppToolboxSlot.hooks";

const STYLE = {
  height: `calc(100vh - var(--top-bar-height))`,
} as React.CSSProperties;

type AppToolboxSlotProps = {
  mainPanelRef: RefObject<HTMLDivElement | null>;
};

export default function AppToolboxSlot({ mainPanelRef }: AppToolboxSlotProps) {
  // RawSync (useSyncExternalStore) re-reads the snapshot on subscribe, covering
  // a page write that lands before the slot subscribes. Default `useAtomValue`
  // snapshots on first paint and only rerenders on a later `store.sub` notify.
  const config = useAtomValueRawSync(toolboxConfigAtom);
  const toolboxApiRef = useRef<ToolboxApiRef>({});
  const handlerRef = useRef<HTMLDivElement>(null);
  const toolboxRef = useRef<HTMLElement | null>(null);
  const { selectedToolbox, isAiEnabled, initialStorageValue } = useAppToolboxState(
    config,
    toolboxApiRef
  );

  const { removeSizes, applyFlexValues } = usePanelResize({
    handlerRef,
    panelsRefs: [mainPanelRef, toolboxRef],
    initialFlexValues: INITIAL_FLEX_VALUES,
    range: TOOLBOX_RESIZE_RANGE,
    disabled: !selectedToolbox,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    if (selectedToolbox) {
      applyFlexValues();
    } else {
      removeSizes();
    }
  }, [!!selectedToolbox, removeSizes, applyFlexValues]);

  if (!isAiEnabled && config.sections.length === 0) {
    return null;
  }

  return (
    <>
      <ResizeHandle
        ref={handlerRef}
        className={cx(
          "sticky top-(--top-bar-height)",
          selectedToolbox ? "has-[+*:empty]:hidden" : "hidden"
        )}
        style={STYLE}
      />
      <Toolbox
        className={cx("empty:hidden sticky top-(--top-bar-height)", {
          "min-w-[350px]": selectedToolbox,
        })}
        style={STYLE}
        ref={toolboxRef}
        apiRef={toolboxApiRef}
        initialValue={initialStorageValue === null ? undefined : initialStorageValue}
        searchParam={TOOLBOX_SEARCH_PARAM}
        cleanupSearchParamsBySection={config.cleanupSearchParamsBySection ?? {}}
      >
        {config.sections.map(
          ({ value, Icon, iconProps, tooltip, Content, contentProps }) => (
            <Toolbox.Section
              key={value}
              value={value}
              icon={() => <Icon {...iconProps} />}
              tooltip={tooltip}
            >
              {/* Type safety enforced at call site via toolboxSection(). Stored type uses
                  ComponentType<any> because the Jotai atom cannot be generic per section. */}
              <ErrorBoundary
                fallback={
                  <div className="p-4 text-error">Toolbox section error</div>
                }
              >
                {createElement(Content, (contentProps ?? {}) as never)}
              </ErrorBoundary>
            </Toolbox.Section>
          )
        )}
        {isAiEnabled && (
          <Toolbox.Section
            value={AI_TOOLBOX_SECTION_VALUE}
            icon={ChatSparkleIcon}
            tooltip="AI Assistant"
          >
            <AiToolboxSection />
          </Toolbox.Section>
        )}
      </Toolbox>
    </>
  );
}
